import React, { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { TimeSelectorRow, ToggleButton } from 'sharedComponents';
import { Graph } from '../ChartView/Graph';
import { getToggleOptions } from './helpers';
import { opButtons } from './constants';
import moment from 'moment';
import { getUTCDateString } from 'helpers/dateHelper';
import { getInitialEndDate, getYesterdayEndDate } from 'sharedComponents/TimeSelectorRow/helpers';
import ErrorView from 'sharedComponents/ErrorBoundary/ErrorView';
import { apiCallStatus } from 'helpers/constants';

const ChartViewComponent = ({
    model: {
        activeTab,
        dateSelection,
        isAdmin,
        chartErrorData
    },
    actions,
    isComparisonView,
    offGraphSignalsCount
}) => {
    const { t: translate } = useTranslation();
    const [toggleOptions] = useState(getToggleOptions());

    const handleSelectDate = (from, to) => {
        if (from && to) {
            actions.setSelectedDates(from, to);
        }
    };

    const getAdjustedDateRange = (activeTab) => {
        let from = dateSelection?.from;
        let to = dateSelection?.to;
        if ([opButtons.scatterPlotV2, opButtons.histogramV2].includes(activeTab)) {
            if (from && to) {
                // Check if 'to' date is today, if yes set it to yesterday's end date
                if (new Date(to).toDateString() === new Date().toDateString()) {
                    const dayDifference = moment(to).diff(moment(from), 'day');
                    const startDate = moment(dayDifference === 1 ? to : from).subtract(1, 'days').startOf('day').toDate();
                    from = getUTCDateString(startDate);
                    to = getYesterdayEndDate();
                }
            }
        } else {
            if (from && to) {
                // Check if 'to' date is yesterday
                if (moment(to).isSame(moment().subtract(1, 'day'), 'day')) {
                    const hoursDifference = moment(to).diff(moment(from), 'hours');
                    if (hoursDifference < 24) {
                        const toDate = moment().toDate();
                        const startDate = moment(toDate).subtract(1, 'days').toDate();
                        from = getUTCDateString(startDate);
                        to = getUTCDateString(toDate);
                    } else {
                        const startDate = moment(from).add(1, 'day').startOf('day').toDate();
                        from = getUTCDateString(startDate);
                        to = getInitialEndDate();
                    }
                }
            }
        }
        handleSelectDate(from, to);
    };

    const handleTabSelection = (tab) => {
        // Removed useCallback due to can't retrieve latest dataselection values
        getAdjustedDateRange(tab);
        actions.setActiveTab(tab);
        actions.resetChart();
    };

    return (
        <Row className='cross-asset-analysis-content'>
            <Col md={12} className='cross-asset-analysis-tab-container'>
                <Row className='button-row-container'>
                    <Col xl={10} sm='auto' md={6}>
                        <ToggleButton
                            options={toggleOptions}
                            onSelected={handleTabSelection}
                            selectedValue={activeTab}
                        />
                    </Col>

                    <Col xl={2} sm='auto' md={6}>
                        <TimeSelectorRow
                            fromDate={dateSelection?.from}
                            toDate={dateSelection?.to}
                            label=''
                            handleSelectDate={handleSelectDate}
                            maxDaysRange={90}
                            maxDaysBack={isAdmin ? 365 : 90}
                            preSelectedDays={7}
                            numberOfMonths={2}
                            excludeToday={[opButtons.histogramV2, opButtons.scatterPlotV2].includes(activeTab)}
                            footerIntervalText={translate('ABB.Powertrain.Frontend.datePickerMax90DaysNote')}
                        />
                    </Col>
                </Row>
                {chartErrorData?.loadingStatus === apiCallStatus.ERROR
                    ? <ErrorView
                        errorCode={chartErrorData?.errorCode || 500}
                        errorMessage={translate('ABB.Powertrain.Frontend.errorBoundaryMessageDesc500')}
                        onReportIssue={() => { }}
                    />
                    : <Graph isComparisonView={isComparisonView} offGraphSignalsCount={offGraphSignalsCount} />
                }
            </Col>
        </Row>
    );
};

export default ChartViewComponent;
ChartViewComponent.propTypes = {
    model: PropTypes.shape({
        activeTab: PropTypes.string,
        dateSelection: PropTypes.shape({
            from: PropTypes.string,
            to: PropTypes.string
        }),
        isAdmin: PropTypes.bool,
        chartErrorData: PropTypes.object
    }),
    actions: PropTypes.shape({
        setActiveTab: PropTypes.func,
        setSelectedDates: PropTypes.func,
        resetChart: PropTypes.func
    }),
    isComparisonView: PropTypes.bool,
    offGraphSignalsCount: PropTypes.number
};
